import type { CallInfo } from '@compass/client/types/call';
import type {
  Evaluation,
  EvaluationFinding,
  EvaluationFindingReview,
  EvaluationReview,
} from '@compass/client/types/evaluation';
import type {
  ActivationRule,
  Scorecard,
  ScorecardQuestion,
  ScorecardSection,
} from '@compass/client/types/scorecard';
import { ScoringType } from '@compass/client/types/scorecard';

import { formatDuration as formatDurationFromSeconds } from '@/utils/date';
import { findTranscriptSegmentIndexForTime, type TranscriptSegment } from '@/utils/transcript';

import { parseEvidenceTextToSnippets } from './evidenceComposer';
import {
  getEvaluationDirectionLabel,
  getMetadataValue,
  SCORE_BAND_EXEMPLARY_MIN,
  SCORE_BAND_MEETS_MIN,
} from './utils';

export { getEvaluationDirectionLabel };

export interface FindingMatch {
  finding: EvaluationFinding;
  index: number;
}

export interface SectionQuestionResult {
  question: ScorecardQuestion;
  finding?: EvaluationFinding;
  findingIndex?: number;
  effectiveFinding?: EvaluationFinding;
}

export interface SectionResult {
  section: ScorecardSection;
  score?: number;
  scoredCount: number;
  questionCount: number;
  questions: SectionQuestionResult[];
}

export interface EvidenceRange {
  start: number;
  end: number;
  segmentIndex?: number;
}

export interface FindingEvidenceSnippet {
  text: string;
  spans: EvidenceRange[];
}

type EvidenceCarrier =
  | Pick<EvaluationFinding, 'evidence'>
  | Pick<EvaluationFindingReview, 'evidence'>;
type RawFindingEvidenceSnippet = {
  spans?: Array<{
    end_seconds?: number | null;
    start_seconds?: number | null;
  }> | null;
  text?: string | null;
};

const SCORE_LABEL_THRESHOLDS = {
  exemplary: SCORE_BAND_EXEMPLARY_MIN,
  meets: SCORE_BAND_MEETS_MIN,
};

export const INLINE_EVIDENCE_TIMESTAMP_PATTERN =
  /\[(\d{1,2}:\d{2}(?::\d{2})?)(?:\s*[–-]\s*(\d{1,2}:\d{2}(?::\d{2})?))?\]/g;
export const LEGACY_MULTI_RANGE_TIMESTAMP_BLOCK_PATTERN =
  /\[\s*\d{1,2}:\d{2}(?::\d{2})?\s*[–-]\s*\d{1,2}:\d{2}(?::\d{2})?(?:\s*,\s*\d{1,2}:\d{2}(?::\d{2})?\s*[–-]\s*\d{1,2}:\d{2}(?::\d{2})?)+\s*\]/g;
const EVIDENCE_RANGE_PATTERN = /(\d{1,2}:\d{2}(?::\d{2})?)\s*[–-]\s*(\d{1,2}:\d{2}(?::\d{2})?)/g;
const EVIDENCE_SINGLE_TIMESTAMP_PATTERN = /(^|[\s[(])(\d{1,2}:\d{2}(?::\d{2})?)(?=$|[\s)\],.])/g;

const DEFAULT_REVIEW_OUTCOME_METADATA_KEYS: string[] = [];
const REVIEW_OUTCOME_METADATA_KEYS = new Set(DEFAULT_REVIEW_OUTCOME_METADATA_KEYS);
// TODO(AQ-516): remove hardcoded keys. skill/campaign/team (AQ-437) are
// call-sourced context, not agent-extracted signals, so they must not appear in
// "Extracted Details" — but they live in the eval metadata map (and in the
// scorecard's valid_metadata_keys, which this allow-list reuses). Hide them here
// as an interim; the real fix is a metadata-registry attribute the UI consults
// instead of hardcoding key names. See https://100ms.atlassian.net/browse/AQ-516
const HIDDEN_METADATA_KEYS = new Set([
  'ae_pqc_detected',
  'skill_name',
  'campaign_name',
  'team_name',
  'queue_name',
]);

// Keys rendered in the hardcoded "Call Details" block — excluded from Extracted Details
const CALL_DETAIL_EXCLUDED_KEYS = new Set([
  'agent_name',
  'agent',
  'representative_name',
  'rep_name',
]);
export interface MetadataFieldConfig {
  key: string;
  label: string;
}

type QcReviewAlertType = 'compliance' | 'escalation';

/** Category of the flagged value: boolean flags vs metadata key-value pairs. */
type QcReviewFlagCategory = 'flag' | 'metadata';

export interface QcReviewAlert {
  type: QcReviewAlertType;
  title: string;
  description: string;
  /** The key used for the review record and the update payload. */
  flagKey: 'escalation' | 'compliance_failed';
  /** Whether this is a boolean flag or a metadata value. */
  flagCategory: QcReviewFlagCategory;
  /** Current value of the flag or metadata field (boolean for flags, string for metadata). */
  currentValue: unknown;
  /** Existing review record if the flag has already been reviewed. */
  review?: EvaluationReview;
}

const DEFAULT_REVIEW_ALERT_DESCRIPTIONS: Record<QcReviewAlert['type'], string> = {
  compliance: 'Critical compliance question failed — manual review required.',
  escalation: 'This review requires escalation follow-up.',
};

const REVIEW_ALERT_TITLES: Record<QcReviewAlert['type'], string> = {
  compliance: 'Compliance Failure',
  escalation: 'Escalation Required',
};

const normalizeMetadataKey = (value: string) =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const getNormalizedRecordEntry = (
  record: Record<string, unknown> | undefined,
  aliases: string[],
): [string, unknown] | null => {
  if (!record) {
    return null;
  }

  const normalizedAliases = new Set(aliases.map(alias => normalizeMetadataKey(alias)));

  for (const alias of aliases) {
    if (alias in record) {
      return [alias, record[alias]];
    }
  }

  for (const [key, value] of Object.entries(record)) {
    if (normalizedAliases.has(normalizeMetadataKey(key))) {
      return [key, value];
    }
  }

  return null;
};

const collectMetadataKeys = (evaluation: Evaluation): string[] => {
  const metadata = evaluation.metadata as Record<string, unknown> | undefined;
  if (!metadata || typeof metadata !== 'object') return [];
  return Object.keys(metadata);
};

const formatMetadataFieldLabel = (key: string) => {
  const normalizedKey = normalizeMetadataKey(key);

  return normalizedKey
    .split('_')
    .filter(Boolean)
    .map(part => {
      if (part === 'ae') {
        return 'AE';
      }
      if (part === 'pqc') {
        return 'PQC';
      }
      if (part === 'phi') {
        return 'PHI';
      }
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ');
};

const getMetadataFieldConfigs = (keys: string[]): MetadataFieldConfig[] => {
  const seen = new Set<string>();

  return keys.reduce<MetadataFieldConfig[]>((result, key) => {
    const normalizedKey = normalizeMetadataKey(key);
    if (!normalizedKey || seen.has(normalizedKey)) {
      return result;
    }

    seen.add(normalizedKey);
    result.push({
      key: normalizedKey,
      label: formatMetadataFieldLabel(key),
    });
    return result;
  }, []);
};

const parseDisplayArrayValue = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value.map(item => String(item).trim()).filter(Boolean);
  }

  if (typeof value === 'string') {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return [];
    }

    if (/\d+[).]\s/.test(trimmedValue)) {
      return trimmedValue
        .split(/\s+(?=\d+[).]\s)/)
        .map(item => item.trim())
        .filter(Boolean);
    }

    if (trimmedValue.includes('\n')) {
      return trimmedValue
        .split(/\n+/)
        .map(item => item.trim())
        .filter(Boolean);
    }

    if (trimmedValue.includes(',')) {
      return trimmedValue
        .split(',')
        .map(item => item.trim())
        .filter(Boolean);
    }

    return [trimmedValue];
  }

  if (typeof value === 'number') {
    return [String(value)];
  }

  if (typeof value === 'boolean') {
    return [value ? 'True' : 'False'];
  }

  return [];
};

const getTimestampFromCall = (
  call: CallInfo | null | undefined,
  primaryKey: 'started_at' | 'ended_at',
  fallbackInputKey: 'call_start_time' | 'call_end_time',
) => {
  const directValue = call?.[primaryKey];
  if (typeof directValue === 'string' && directValue.trim()) {
    return directValue;
  }

  const inputValue = call?.input_data?.[fallbackInputKey];
  return typeof inputValue === 'string' && inputValue.trim() ? inputValue : null;
};

// durationBetween returns whole seconds when both timestamps are present, valid, and
// end is after start; otherwise null.
const durationBetween = (
  start: string | null | undefined,
  end: string | null | undefined,
): number | null => {
  if (!start || !start.trim() || !end || !end.trim()) {
    return null;
  }
  const startMs = new Date(start).getTime();
  const endMs = new Date(end).getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) {
    return null;
  }
  const seconds = Math.floor((endMs - startMs) / 1000);
  return seconds > 0 ? seconds : null;
};

const getActivationRuleValues = (rule: ActivationRule, evaluation: Evaluation): string[] => {
  const metadata = evaluation.metadata as Record<string, unknown> | undefined;
  const matchedEntry = getNormalizedRecordEntry(metadata, [rule.fact || '']);
  return parseDisplayArrayValue(matchedEntry?.[1]);
};

const normalizeDisplayValues = (values: string[]) =>
  values.map(value => normalizeMetadataKey(value));

const matchesCallDirectionRule = (rule: ActivationRule, evaluation: Evaluation) => {
  const expected = normalizeMetadataKey(String(rule.value || ''));
  const actual = normalizeMetadataKey(getEvaluationDirectionLabel(evaluation.call_direction) || '');

  if (rule.operator === '==') {
    return !!actual && actual === expected;
  }

  return true;
};

const matchesMetadataRule = (rule: ActivationRule, evaluation: Evaluation) => {
  const values = normalizeDisplayValues(getActivationRuleValues(rule, evaluation));
  if (values.length === 0) {
    return false;
  }

  const expectedValues = normalizeDisplayValues(parseDisplayArrayValue(rule.value));

  switch (rule.operator) {
    case 'exists':
      return true; // values.length > 0 is guaranteed by the guard above
    case '==':
    case 'in':
      return expectedValues.length > 0 && expectedValues.some(value => values.includes(value));
    default:
      return true;
  }
};

const matchesActivationRule = (rule: ActivationRule, evaluation: Evaluation): boolean => {
  if (!rule.fact || !rule.operator) {
    return true;
  }

  if (rule.source === 'call_direction') {
    return matchesCallDirectionRule(rule, evaluation);
  }

  return matchesMetadataRule(rule, evaluation);
};

export type QcScoreTone = 'muted' | 'exemplary' | 'meets' | 'needs_coaching';
export type QcScoreToneIntensity = 'default' | 'muted';

const QC_SCORE_TONE_CONFIG: Record<
  QcScoreTone,
  {
    content: string;
    textClass: string;
    toneClassByIntensity: Record<QcScoreToneIntensity, string>;
  }
> = {
  muted: {
    content: '--',
    textClass: 'text-muted-foreground',
    toneClassByIntensity: {
      default: 'bg-muted text-muted-foreground',
      muted: 'bg-muted text-muted-foreground',
    },
  },
  exemplary: {
    content: 'Exemplary',
    textClass: 'text-positive-foreground',
    toneClassByIntensity: {
      default: 'bg-positive text-positive-foreground',
      muted: 'bg-positive-muted text-positive-foreground',
    },
  },
  meets: {
    content: 'Meets Expectations',
    textClass: 'text-notice-foreground',
    toneClassByIntensity: {
      default: 'bg-notice text-notice-foreground',
      muted: 'bg-notice-muted text-notice-foreground',
    },
  },
  needs_coaching: {
    content: 'Unsatisfactory',
    textClass: 'text-attention-foreground',
    toneClassByIntensity: {
      default: 'bg-attention text-attention-foreground',
      muted: 'bg-attention-muted text-attention-foreground',
    },
  },
};

export const getQcScoreTone = (score?: number): QcScoreTone => {
  if (score === undefined || score === null) {
    return 'muted';
  }

  if (score >= SCORE_LABEL_THRESHOLDS.exemplary) {
    return 'exemplary';
  }

  if (score >= SCORE_LABEL_THRESHOLDS.meets) {
    return 'meets';
  }

  return 'needs_coaching';
};

export const getQcScoreToneClass = (score?: number, intensity: QcScoreToneIntensity = 'muted') =>
  QC_SCORE_TONE_CONFIG[getQcScoreTone(score)].toneClassByIntensity[intensity];

export const getQcScoreLabel = (score?: number) => {
  const tone = getQcScoreTone(score);
  const config = QC_SCORE_TONE_CONFIG[tone];

  return {
    content: config.content,
    toneClass: config.toneClassByIntensity.default,
    textClass: config.textClass,
  };
};

export const getQcScoreToneClasses = (score?: number) => getQcScoreLabel(score).textClass;

const parseEvidenceTimestampToSeconds = (value: string): number | null => {
  const parts = value.split(':').map(part => Number.parseInt(part, 10));
  if (parts.some(part => Number.isNaN(part))) {
    return null;
  }

  if (parts.length === 2) {
    return parts[0] * 60 + parts[1];
  }

  if (parts.length === 3) {
    return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }

  return null;
};

const normalizeEvidenceRange = (start: number, end?: number): EvidenceRange | null => {
  if (!Number.isFinite(start) || start < 0) {
    return null;
  }

  const resolvedEnd = Number.isFinite(end) && end! > start ? end! : start + 1;
  return {
    start,
    end: resolvedEnd,
  };
};

const dedupeEvidenceRanges = (ranges: EvidenceRange[]): EvidenceRange[] => {
  const seen = new Set<string>();

  return ranges.filter(range => {
    const key = `${range.start}:${range.end}`;
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const normalizeStructuredEvidenceSpan = (span: {
  start_seconds?: number | null;
  end_seconds?: number | null;
}): EvidenceRange | null => {
  const hasStart = span.start_seconds != null;
  const hasEnd = span.end_seconds != null;

  // Truly malformed: neither boundary is present
  if (!hasStart && !hasEnd) {
    return null;
  }

  // Proto omitzero: start_seconds=0 is omitted, default to 0 when end is present
  const startSeconds = span.start_seconds ?? 0;

  return normalizeEvidenceRange(startSeconds, span.end_seconds ?? undefined);
};

const parseMatchedEvidenceRanges = (
  snippet: string,
  pattern: RegExp,
  parseRange: (match: RegExpMatchArray) => EvidenceRange | null,
): EvidenceRange[] => {
  const ranges: EvidenceRange[] = [];

  for (const match of snippet.matchAll(pattern)) {
    const range = parseRange(match);
    if (range) {
      ranges.push(range);
    }
  }

  return dedupeEvidenceRanges(ranges);
};

const parseLegacyExplicitRanges = (snippet: string): EvidenceRange[] =>
  parseMatchedEvidenceRanges(snippet, EVIDENCE_RANGE_PATTERN, match => {
    const start = parseEvidenceTimestampToSeconds(match[1]);
    const end = parseEvidenceTimestampToSeconds(match[2]);
    if (start === null || end === null) {
      return null;
    }

    return normalizeEvidenceRange(start, end);
  });

const parseLegacySingleTimestampRanges = (snippet: string): EvidenceRange[] =>
  parseMatchedEvidenceRanges(snippet, EVIDENCE_SINGLE_TIMESTAMP_PATTERN, match => {
    const start = parseEvidenceTimestampToSeconds(match[2]);
    if (start === null) {
      return null;
    }

    return normalizeEvidenceRange(start);
  });

const getLegacyEvidenceRanges = (source?: EvidenceCarrier): EvidenceRange[] => {
  const evidence = source?.evidence;
  if (!evidence) {
    return [];
  }

  const snippet = evidence.snippet?.trim();
  if (!snippet) {
    return [];
  }

  const parsedRanges = parseLegacyExplicitRanges(snippet);
  if (parsedRanges.length > 0) {
    return parsedRanges;
  }

  // Only parse standalone timestamps when no ranges were found, so a range like
  // "7:13–8:10" does not also create a duplicate single-timestamp pill for 7:13.
  return parseLegacySingleTimestampRanges(snippet);
};

const normalizeFindingEvidenceSnippet = (
  snippet: RawFindingEvidenceSnippet,
): FindingEvidenceSnippet => ({
  text: snippet.text?.trim() || '',
  spans: dedupeEvidenceRanges(
    (snippet.spans || [])
      .map(span => normalizeStructuredEvidenceSpan(span))
      .filter((range): range is EvidenceRange => range !== null),
  ),
});

const normalizeFindingEvidenceSnippets = (
  snippets?: RawFindingEvidenceSnippet[] | null,
): FindingEvidenceSnippet[] =>
  (snippets || [])
    .map(normalizeFindingEvidenceSnippet)
    .filter(snippet => snippet.text || snippet.spans.length > 0);

export const getFindingEvidenceSnippets = (source?: EvidenceCarrier): FindingEvidenceSnippet[] => {
  const evidence = source?.evidence;
  if (!evidence) {
    return [];
  }

  const structuredSnippets = normalizeFindingEvidenceSnippets(evidence.snippets);
  if (structuredSnippets.length > 0) {
    return structuredSnippets;
  }

  const legacyText = evidence.snippet?.trim() || '';
  const composerSnippets = normalizeFindingEvidenceSnippets(
    parseEvidenceTextToSnippets(legacyText),
  );

  if (composerSnippets.some(snippet => snippet.spans.length > 0)) {
    return composerSnippets;
  }

  const legacySpans = getLegacyEvidenceRanges(source);

  if (!legacyText && legacySpans.length === 0) {
    return [];
  }

  return [
    {
      text: legacyText,
      spans: legacySpans,
    },
  ];
};

export const resolveEvidenceHighlightRange = (
  transcriptSegments: TranscriptSegment[],
  startSeconds: number,
  endSeconds?: number,
): EvidenceRange => {
  const requestedRange = normalizeEvidenceRange(startSeconds, endSeconds) ?? {
    start: 0,
    end: 1,
  };

  const resolvedSegmentIndex = findTranscriptSegmentIndexForTime(
    transcriptSegments,
    requestedRange.start,
  );
  if (resolvedSegmentIndex >= 0) {
    const resolvedSegment = transcriptSegments[resolvedSegmentIndex];
    const isPointTimestamp = !(Number.isFinite(endSeconds) && endSeconds! > startSeconds);
    return {
      start: resolvedSegment.start,
      end: resolvedSegment.end,
      ...(isPointTimestamp ? { segmentIndex: resolvedSegmentIndex } : {}),
    };
  }

  return requestedRange;
};

export const getCallDateTimeValue = (call?: CallInfo | null) => {
  return getTimestampFromCall(call, 'started_at', 'call_start_time') || null;
};

export const formatDurationFromCall = (call?: CallInfo | null) => {
  // Mirror the backend analytics duration (service/analytics.go: callModelDurationSeconds):
  // use the typed started_at/ended_at pair only when BOTH yield a positive duration,
  // otherwise fall back to the input_data.call_start_time/call_end_time pair.
  const seconds =
    durationBetween(call?.started_at, call?.ended_at) ??
    durationBetween(call?.input_data?.call_start_time, call?.input_data?.call_end_time);

  return seconds == null ? null : formatDurationFromSeconds(seconds);
};

const findFindingMatchByMetricKey = (
  findings: EvaluationFinding[] = [],
): Map<string, FindingMatch> => {
  const findingByMetricKey = new Map<string, FindingMatch>();

  findings.forEach((finding, index) => {
    if (!finding.metric_key || findingByMetricKey.has(finding.metric_key)) {
      return;
    }

    findingByMetricKey.set(finding.metric_key, { finding, index });
  });

  return findingByMetricKey;
};

const hasReviewerEvidence = (review?: EvaluationFindingReview) => {
  if (!review?.evidence) {
    return false;
  }

  return !!review.evidence.snippet?.trim() || (review.evidence.snippets?.length ?? 0) > 0;
};

export const getReviewerEffectiveFindings = (
  findings: EvaluationFinding[] = [],
  findingReviews: Record<string, EvaluationFindingReview> | undefined = {},
): EvaluationFinding[] => {
  if (!Object.keys(findingReviews).length) {
    return findings;
  }

  const effectiveFindings = new Map<string, EvaluationFinding>();

  findings.forEach(finding => {
    if (!finding.metric_key || effectiveFindings.has(finding.metric_key)) {
      return;
    }

    effectiveFindings.set(finding.metric_key, { ...finding });
  });

  Object.entries(findingReviews).forEach(([metricKey, review]) => {
    if (!review) {
      return;
    }

    const current = effectiveFindings.get(metricKey);
    const nextFinding: EvaluationFinding = current ? { ...current } : { metric_key: metricKey };

    if (review.raw_score !== undefined && review.raw_score !== null) {
      nextFinding.raw_score = review.raw_score;
    }

    if (hasReviewerEvidence(review)) {
      nextFinding.evidence = review.evidence;
    }

    effectiveFindings.set(metricKey, nextFinding);
  });

  return findings.map(finding => {
    if (!finding.metric_key) {
      return finding;
    }
    return effectiveFindings.get(finding.metric_key) ?? finding;
  });
};

const questionMatchesCallDirection = (question: ScorecardQuestion, evaluation?: Evaluation) => {
  if (!evaluation?.call_direction || (question.call_directions?.length || 0) === 0) {
    return true;
  }

  return question.call_directions?.includes(evaluation.call_direction) ?? true;
};

// MAX_RULE_DEPTH must match the backend validator's limit in
// service/scorecards.go (validateActivationRules). Trees deeper than this
// should never reach the frontend because the API rejects them at write
// time; the guard here is defense-in-depth against corrupted or injected
// data that could otherwise blow the browser stack.
const MAX_RULE_DEPTH = 5;

const evaluateRule = (rule: ActivationRule, evaluation: Evaluation, depth = 0): boolean => {
  if (depth > MAX_RULE_DEPTH) {
    // Fail open: surface the question rather than silently hiding a QC
    // criterion. Matches matchesActivationRule's behavior for unknown
    // operators — "if we can't evaluate, show it."

    console.warn('activation rule tree exceeds max depth; treating as matched', {
      operator: rule.operator,
      depth,
    });
    return true;
  }
  // Branch node (combinator)
  if (rule.operator === 'or') {
    const children = Array.isArray(rule.value) ? rule.value : [];
    return children.some(child => evaluateRule(child, evaluation, depth + 1));
  }
  if (rule.operator === 'and') {
    const children = Array.isArray(rule.value) ? rule.value : [];
    return children.every(child => evaluateRule(child, evaluation, depth + 1));
  }
  if (rule.operator === 'not') {
    const children = Array.isArray(rule.value) ? rule.value : [];
    if (children.length === 0) return true; // Empty NOT is true
    // NOT with multiple children: negate the result of ANDing them
    return !children.every(child => evaluateRule(child, evaluation, depth + 1));
  }
  // Leaf node (predicate)
  return matchesActivationRule(rule, evaluation);
};

const questionMatchesActivationRules = (question: ScorecardQuestion, evaluation?: Evaluation) => {
  if (!evaluation || (question.activation_rules?.length || 0) === 0) {
    return true;
  }

  return (question.activation_rules || []).every(rule => evaluateRule(rule, evaluation));
};

const isQuestionApplicable = (
  question: ScorecardQuestion,
  findingByMetricKey: Map<string, FindingMatch>,
  evaluation?: Evaluation,
) => {
  const match = question.metric_key ? findingByMetricKey.get(question.metric_key) : undefined;
  if (match) {
    return true;
  }

  return (
    questionMatchesCallDirection(question, evaluation) &&
    questionMatchesActivationRules(question, evaluation)
  );
};

export const computeScorecardSectionResults = (
  sections: ScorecardSection[] = [],
  findings: EvaluationFinding[] = [],
  evaluation?: Evaluation,
  effectiveFindings: EvaluationFinding[] = findings,
) => {
  const findingByMetricKey = findFindingMatchByMetricKey(findings);
  const effectiveFindingByMetricKey = findFindingMatchByMetricKey(effectiveFindings);

  return sections.map<SectionResult>(section => {
    let weightedScoreSum = 0;
    let weightedScoreCount = 0;

    const questions = (section.questions || [])
      .filter(question => isQuestionApplicable(question, effectiveFindingByMetricKey, evaluation))
      .map(question => {
        const match = question.metric_key ? findingByMetricKey.get(question.metric_key) : undefined;
        const effectiveMatch = question.metric_key
          ? effectiveFindingByMetricKey.get(question.metric_key)
          : undefined;
        const score = normalizeQuestionRawScore(
          effectiveMatch?.finding.raw_score,
          question.scoring_type,
        );
        const weight = question.weight ?? 1;

        weightedScoreSum += score * weight;
        weightedScoreCount += weight;

        return {
          question,
          finding: match?.finding,
          findingIndex: match?.index,
          effectiveFinding: effectiveMatch?.finding,
        };
      });

    return {
      section,
      score: weightedScoreCount > 0 ? weightedScoreSum / weightedScoreCount : undefined,
      // Finding presence (not raw_score presence) signals "evaluated": protojson omits
      // zero-valued scalars, so a finding scored 0 has no raw_score key on the wire.
      scoredCount: questions.filter(item => item.effectiveFinding !== undefined).length,
      questionCount: questions.length,
      questions,
    };
  });
};

// Normalise a raw LLM score to the 0-1 range expected by each scoring type.
// This must stay in sync with the backend's normalizeScore (ethos/service/scoring.go).
export const normalizeQuestionRawScore = (
  rawScore: number | null | undefined,
  scoringType?: ScoringType,
): number => {
  const score = rawScore ?? 0;
  switch (scoringType) {
    case ScoringType.SCORING_TYPE_BINARY:
      return score > 0 ? 1 : 0;
    case ScoringType.SCORING_TYPE_SCALE:
      if (score >= 1) return 1;
      if (score > 0) return 0.5;
      return 0;
    default:
      return score > 0 ? 1 : 0;
  }
};

export const getQuestionScoreLabel = (
  finding?: EvaluationFinding,
  scoringType?: ScoringType,
  weight: number = 1,
): string => {
  // No finding = AI skipped this question entirely — not evaluated.
  if (!finding) {
    return '--';
  }

  // Finding exists — missing raw_score is implicitly 0 (protobuf omits defaults).
  const raw = normalizeQuestionRawScore(finding.raw_score, scoringType);
  const weightedScore = raw * weight;
  const formattedScore = Number.isInteger(weightedScore)
    ? String(weightedScore)
    : String(Number(weightedScore.toFixed(2)));
  const formattedWeight = Number.isInteger(weight)
    ? String(weight)
    : String(Number(weight.toFixed(2)));

  return `${formattedScore}/${formattedWeight}`;
};

const getScaleAnswerLabel = (raw: number): string => {
  if (raw >= 1) return 'Yes';
  if (raw > 0) return 'Partial';
  return 'No';
};

export const getQuestionAnswerLabel = (
  finding?: EvaluationFinding,
  scoringType?: ScoringType,
): string | null => {
  // No finding = AI skipped this question entirely — nothing to display.
  if (!finding) {
    return null;
  }

  // Finding exists — missing raw_score is implicitly 0 (protobuf omits defaults).
  const raw = normalizeQuestionRawScore(finding.raw_score, scoringType);

  if (scoringType === ScoringType.SCORING_TYPE_SCALE) {
    return getScaleAnswerLabel(raw);
  }

  // Binary or unspecified scoring type
  return raw >= 1 ? 'Yes' : 'No';
};

const getMappedAgentName = (userNameById: Map<string, string>, userId?: string | null) => {
  if (!userId || !userNameById.has(userId)) {
    return null;
  }

  return userNameById.get(userId) || null;
};

export const getAgentNameForEvaluation = (
  evaluation: Evaluation,
  userNameById: Map<string, string>,
  call?: CallInfo | null,
) => {
  const evaluationAgentName = getMappedAgentName(userNameById, evaluation.agent_user_id);
  if (evaluationAgentName) return evaluationAgentName;

  const callAgentNameById = getMappedAgentName(userNameById, call?.user_id);
  if (callAgentNameById) return callAgentNameById;

  const callAgentName = call?.input_data?.agent_name;
  if (typeof callAgentName === 'string' && callAgentName.trim()) {
    return callAgentName.trim();
  }

  return getMetadataValue(evaluation, ['agent_name', 'agent', 'representative_name', 'rep_name']);
};

const getStringValueFromRecord = (
  record: Record<string, unknown> | undefined,
  aliases: string[],
) => {
  const matchedEntry = getNormalizedRecordEntry(record, aliases);
  const value = matchedEntry?.[1];

  if (typeof value === 'string' && value.trim()) {
    return value.trim();
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (typeof value === 'boolean') {
    return value ? 'True' : 'False';
  }

  return null;
};
export const getSummaryFieldValue = (evaluation: Evaluation, aliases: string[]) => {
  const metadata = evaluation.metadata as Record<string, unknown> | undefined;
  return getStringValueFromRecord(metadata, aliases) ?? null;
};

export const getSummaryFieldArrayValue = (evaluation: Evaluation, aliases: string[]): string[] => {
  const metadata = evaluation.metadata as Record<string, unknown> | undefined;
  const matchedEntry = getNormalizedRecordEntry(metadata, aliases);
  return parseDisplayArrayValue(matchedEntry?.[1]);
};

export const formatMetadataValue = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed || trimmed.includes(' ')) return trimmed;
  // Only normalize values that look like identifiers (lowercase, underscores, hyphens)
  if (/^[a-z0-9_-]+$/.test(trimmed)) {
    return formatMetadataFieldLabel(trimmed);
  }
  return trimmed;
};

const isExtractedDetailKey = (key: string, allowedKeys: Set<string> | null): boolean =>
  (!allowedKeys || allowedKeys.has(key)) &&
  !REVIEW_OUTCOME_METADATA_KEYS.has(key) &&
  !HIDDEN_METADATA_KEYS.has(key) &&
  !CALL_DETAIL_EXCLUDED_KEYS.has(key);

const buildAllowedKeysSet = (scorecard: Scorecard | null | undefined): Set<string> | null => {
  if (!scorecard?.valid_metadata_keys?.length) return null;
  return new Set(scorecard.valid_metadata_keys.map(normalizeMetadataKey).filter(Boolean));
};

export const getExtractedDetailFields = (
  scorecard: Scorecard | null | undefined,
  evaluation: Evaluation,
): MetadataFieldConfig[] => {
  const evalMetadata = evaluation.metadata as Record<string, unknown> | undefined;
  if (!evalMetadata || Object.keys(evalMetadata).length === 0) return [];

  const allowedKeys = buildAllowedKeysSet(scorecard);

  const seen = new Set<string>();
  const result: MetadataFieldConfig[] = [];

  for (const rawKey of collectMetadataKeys(evaluation)) {
    const normalizedKey = normalizeMetadataKey(rawKey);
    if (!normalizedKey || seen.has(normalizedKey)) continue;
    seen.add(normalizedKey);

    if (!isExtractedDetailKey(normalizedKey, allowedKeys)) continue;

    const entry = getNormalizedRecordEntry(evalMetadata, [normalizedKey]);
    if (parseDisplayArrayValue(entry?.[1]).length > 0) {
      result.push({ key: normalizedKey, label: formatMetadataFieldLabel(normalizedKey) });
    }
  }

  return result;
};

export const getMetadataReasonValue = (evaluation: Evaluation, key: string): string | null => {
  const reasonEntry = getNormalizedRecordEntry(
    evaluation.reasons?.metadata as Record<string, unknown> | undefined,
    [key],
  );
  const reason = reasonEntry?.[1];
  return typeof reason === 'string' && reason.trim() ? reason.trim() : null;
};

export const getReviewOutcomeMetadataFields = (
  scorecard?: Scorecard | null,
): MetadataFieldConfig[] => {
  const keys = scorecard?.valid_metadata_keys?.length
    ? scorecard.valid_metadata_keys.filter(key =>
        REVIEW_OUTCOME_METADATA_KEYS.has(normalizeMetadataKey(key)),
      )
    : DEFAULT_REVIEW_OUTCOME_METADATA_KEYS;

  const configs = getMetadataFieldConfigs(keys);
  return configs;
};

interface BuildReviewAlertArgs {
  enabled: boolean | undefined;
  type: QcReviewAlertType;
  flagKey: QcReviewAlert['flagKey'];
  flagCategory: QcReviewFlagCategory;
  currentValue: unknown;
  review?: EvaluationReview;
  description?: string;
}

const buildReviewAlert = (args: BuildReviewAlertArgs): QcReviewAlert | null => {
  if (args.enabled !== true) {
    return null;
  }

  return {
    type: args.type,
    title: REVIEW_ALERT_TITLES[args.type],
    description: args.description?.trim() || DEFAULT_REVIEW_ALERT_DESCRIPTIONS[args.type],
    flagKey: args.flagKey,
    flagCategory: args.flagCategory,
    currentValue: args.currentValue,
    review: args.review,
  };
};

const isFlagUnreviewed = (flagValue: boolean | undefined, review: unknown): boolean =>
  flagValue === true && !review;

/**
 * Counts the number of active flags/metadata values that have NOT yet been reviewed.
 * Used by the UI to determine when to set needs_review=false (on the last review).
 */
export const countUnreviewedFlags = (evaluation: Evaluation): number => {
  const flags = evaluation.flags;
  const reviews = evaluation.reviews;
  let count = 0;
  if (isFlagUnreviewed(flags?.escalation, reviews?.escalation)) count++;
  if (isFlagUnreviewed(flags?.compliance_failed, reviews?.compliance_failed)) count++;
  return count;
};

const buildComplianceAlert = (evaluation: Evaluation): QcReviewAlert | null =>
  buildReviewAlert({
    enabled: evaluation.flags?.compliance_failed,
    type: 'compliance',
    flagKey: 'compliance_failed',
    flagCategory: 'flag',
    currentValue: true,
    review: evaluation.reviews?.compliance_failed,
    description: evaluation.reasons?.compliance_failed,
  });

const buildEscalationAlert = (evaluation: Evaluation): QcReviewAlert | null =>
  buildReviewAlert({
    enabled: evaluation.flags?.escalation,
    type: 'escalation',
    flagKey: 'escalation',
    flagCategory: 'flag',
    currentValue: true,
    review: evaluation.reviews?.escalation,
    description: evaluation.reasons?.escalation,
  });

export const getQcReviewAlerts = (evaluation: Evaluation): QcReviewAlert[] =>
  [buildComplianceAlert(evaluation), buildEscalationAlert(evaluation)].filter(
    (alert): alert is QcReviewAlert => alert !== null,
  );

/**
 * Visibility is driven by the current flag value only. Review state is passed
 * through so the alert body can decide how to present confirmed or dismissed
 * reviews without hiding a newly active flag.
 */
export const getVisibleQcReviewAlerts = (evaluation: Evaluation): QcReviewAlert[] =>
  getQcReviewAlerts(evaluation);
